// src/services/api.js
import axios from "axios";
import { setServerVersion } from "../versionState";
import { shouldBlockProtectedRequest } from "./billingGuard";
import { setCacheScope } from "./cache";
import { resolveApiBaseUrl } from "./apiBaseUrl";

const baseURL = resolveApiBaseUrl({
  configured: process.env.REACT_APP_REPORT360_API_BASE_URL,
  hostname: window.location.hostname,
});

// 2) Axios instance
const api = axios.create({
  baseURL,
  withCredentials: true,
});
const baseAdapter = axios.getAdapter(api.defaults.adapter);

// -----------------------------------------------------------------------------
// Access token handling (industry-standard SPA pattern)
// - Access JWT: short-lived, kept in memory (not localStorage)
// - Refresh token: httpOnly cookie (set by backend on /auth/login + /auth/refresh)
// -----------------------------------------------------------------------------
let accessToken = "";
let refreshPromise = null;
let refreshTimer = null;
let demoRuntime = null;
let clientBillingGuard = { locked: false, reason: "", billing: null, role: "" };
const REFRESH_SKEW_SECONDS = 60;

function makeDemoResponse(config, mocked) {
  return {
    data: mocked?.data ?? mocked,
    status: mocked?.status ?? 200,
    statusText: mocked?.statusText ?? "OK",
    headers: mocked?.headers || {},
    config,
    request: { demo: true },
  };
}

function _safeParseJwtExp(tok) {
  try {
    const parts = String(tok || "").split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp);
    return Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

function _safeParseJwtPayload(tok) {
  try {
    const parts = String(tok || "").split(".");
    if (parts.length !== 3) return null;
    return JSON.parse(atob(parts[1]));
  } catch {
    return null;
  }
}

function _cacheScopeFromToken(tok) {
  const payload = _safeParseJwtPayload(tok);
  if (payload) {
    const tenant = payload.tid || payload.tenant_id || payload.tenant || "unknown";
    const user = payload.sub || payload.uid || payload.user_id || payload.email || "unknown";
    return `tenant:${tenant}:user:${user}`;
  }
  const fallback = String(tok || "").slice(-16);
  return fallback ? `token:${fallback}` : "anonymous";
}

function _clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function _scheduleProactiveRefresh(tok) {
  _clearRefreshTimer();
  const exp = _safeParseJwtExp(tok);
  if (!exp) return;
  const msUntil = exp * 1000 - Date.now() - REFRESH_SKEW_SECONDS * 1000;
  if (msUntil <= 0) return;
  refreshTimer = setTimeout(async () => {
    try {
      await refreshSessionSingleFlight();
    } catch {
      await logoutSession();
      try {
        window.location.replace("/login");
      } catch {
        // ignore
      }
    }
  }, msUntil);
}

export function setAccessToken(tok) {
  accessToken = String(tok || "");
  setCacheScope(accessToken ? _cacheScopeFromToken(accessToken) : "anonymous");
  if (accessToken) {
    _scheduleProactiveRefresh(accessToken);
  } else {
    _clearRefreshTimer();
  }
}
export function getAccessToken() {
  return accessToken;
}
export function clearAccessToken() {
  accessToken = "";
  setCacheScope("anonymous");
  _clearRefreshTimer();
}

export function setDemoRuntime(runtime) {
  demoRuntime = runtime || null;
}

export function clearDemoRuntime() {
  demoRuntime = null;
}

export function setClientBillingGuard(nextGuard) {
  const role = String(nextGuard?.role || "").toLowerCase();
  clientBillingGuard = {
    locked: role === "super" ? false : Boolean(nextGuard?.locked),
    reason: String(nextGuard?.reason || ""),
    billing: nextGuard?.billing || null,
    role,
  };
}

export function getClientBillingGuard() {
  return clientBillingGuard;
}

function createBillingGuardError(config) {
  const error = new Error("Subscription required");
  error.isAxiosError = true;
  error.config = config;
  error.response = {
    status: 402,
    data: {
      code: clientBillingGuard.reason || "SUBSCRIPTION_REQUIRED",
      message: "Buy/Renew subscription to view dashboards",
      billing: clientBillingGuard.billing || {},
      source: "client_guard",
    },
    config,
    headers: {},
  };
  return error;
}

// Raw client without auth interceptors (used for refresh)
const raw = axios.create({ baseURL, withCredentials: true });

export async function recordPageVisit(payload = {}) {
  try {
    await raw.post("/public/page-visit", payload, { timeout: 2500 });
  } catch {
    // Public analytics must never block page rendering.
  }
}

api.defaults.adapter = async (config) => {
  if (demoRuntime?.enabled && typeof demoRuntime.handle === "function") {
    const mocked = await demoRuntime.handle(config);
    if (mocked) {
      return makeDemoResponse(config, mocked);
    }
  }
  return baseAdapter(config);
};

export async function refreshSession() {
  const { data } = await raw.post("/auth/refresh", {});
  if (data?.access_token) setAccessToken(data.access_token);
  return data;
}

async function refreshSessionSingleFlight() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      return await refreshSession();
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function logoutSession() {
  try {
    await raw.post("/auth/logout", {});
  } finally {
    clearAccessToken();
  }
}

// 3) Attach access JWT to every request
api.interceptors.request.use((cfg) => {
  const requestUrl = String(cfg?.url || "");
  if (shouldBlockProtectedRequest(requestUrl, clientBillingGuard)) {
    return Promise.reject(createBillingGuardError(cfg));
  }
  if (accessToken) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${accessToken}`;
  }
  return cfg;
});

// 4) Central response handler: version tracking + auth errors
api.interceptors.response.use(
  (resp) => {
    // Best-effort: capture backend version header on every response
    try {
      const headers = resp && resp.headers;
      if (headers) {
        const v = headers["x-app-version"] || headers["X-App-Version"];
        if (typeof v === "string" && v) {
          setServerVersion(v);
        }
      }
    } catch {
      // Never block responses due to version handling
    }
    return resp;
  },
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;

    // Attempt one transparent refresh+retry on 401s (except for auth endpoints themselves)
    if (status === 401 && original && !original.__isRetry) {
      const url = String(original.url || "");
      if (!url.startsWith("/auth/")) {
        original.__isRetry = true;
        try {
          await refreshSessionSingleFlight();
          original.headers = original.headers || {};
          if (accessToken) original.headers.Authorization = `Bearer ${accessToken}`;
          return api(original);
        } catch {
          await logoutSession();
          try {
            window.location.replace("/login");
          } catch {
            // ignore
          }
        }
      }
    }

    const errCode = err?.response?.data?.code || err?.response?.data?.detail?.code;
    if (status === 402 && errCode !== "AI_CREDITS_REQUIRED") {
      try {
        const currentPath = String(window.location.pathname || "");
        if (currentPath.startsWith("/dashboard") && !currentPath.startsWith("/dashboard/settings")) {
          window.location.replace("/dashboard/settings?tab=subscription");
        }
      } catch {
        // ignore
      }
    }

    return Promise.reject(err);
  }
);

// 5) whoAmI helper
export const whoAmI = async () => {
  const { data } = await api.get("/whoami");
  return data;
};

export const getIngestStatus = async ({ compIds = [], signal } = {}) => {
  const params = new URLSearchParams();
  if (Array.isArray(compIds) && compIds.length) {
    params.set("comp_ids", compIds.join(","));
  }
  params.set("_", String(Date.now()));
  const suffix = params.toString();
  const { data } = await api.get(`/dash/ingest/status${suffix ? `?${suffix}` : ""}`, { signal });
  return data;
};

export async function createSupportTicket({ subject, description, dashboard, dashboardRoute, screenshots = [] }) {
  const { data } = await api.post("/support/tickets", {
    subject,
    description,
    dashboard,
    dashboard_route: dashboardRoute,
    screenshots,
  });
  return data;
}

export const signupTally = async ({ email, password, confirmPassword, licenseNumber }) => {
  const { data } = await api.post("/auth/signup-tally", {
    email,
    password,
    confirm_password: confirmPassword,
    license_number: licenseNumber,
  });
  return data;
};

export const firebaseLogin = async ({ idToken }) => {
  const { data } = await api.post("/auth/firebase-login", {
    id_token: idToken,
  });
  if (data?.access_token) setAccessToken(data.access_token);
  return data;
};

export const signupTallyWithFirebaseToken = async ({
  idToken,
  licenseNumber,
  generateLicense = false,
  market,
  referralEmail,
  referralCode,
  signupDraftId,
  name,
  companyName,
  mobile,
}) => {
  const payload = {
    id_token: idToken,
  };
  if (market) {
    payload.market = market;
  }
  if (name) {
    payload.name = name;
  }
  if (companyName) {
    payload.company_name = companyName;
  }
  if (mobile) {
    payload.mobile = mobile;
  }
  if (referralEmail) {
    payload.referral_email = referralEmail;
  }
  if (referralCode) {
    payload.referral_code = referralCode;
  }
  if (signupDraftId) {
    payload.signup_draft_id = signupDraftId;
  }
  if (generateLicense) {
    payload.generate_license = true;
  } else if (licenseNumber) {
    payload.license_number = licenseNumber;
  }
  const { data } = await api.post("/auth/firebase-signup-tally", payload);
  if (data?.access_token) setAccessToken(data.access_token);
  return data;
};

export const createSignupDraft = async ({
  name,
  companyName,
  mobile,
  email,
  market,
  referralEmail,
  referralCode,
  acceptedNotice,
  next,
}) => {
  const { data } = await api.post("/auth/signup-draft", {
    name,
    company_name: companyName,
    mobile,
    email,
    market,
    referral_email: referralEmail || undefined,
    referral_code: referralCode || undefined,
    accepted_notice: Boolean(acceptedNotice),
    next_path: next || "/dashboard",
  });
  return data;
};

export const getSignupDraft = async (signupDraftId) => {
  const { data } = await api.get(`/auth/signup-draft/${encodeURIComponent(signupDraftId)}`);
  return data;
};

export const switchTenantToken = async ({ tenantId, licenseNumber }) => {
  const { data } = await api.post("/auth/switch-tenant", {
    tenant_id: tenantId || undefined,
    license_number: licenseNumber || undefined,
  });
  if (data?.access_token) setAccessToken(data.access_token);
  return data;
};

export default api;
